import {Action, DataStatus} from 'platform/components';
import {Text, VStack} from 'platform/foundation';

import {isEmpty, isNotNil} from 'ramda';

import {
  TelephonyGroup,
  useGetTelephonyGroupsQuery,
  useGetTelephonyLinesQuery,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {usePermissions} from '@dms/shared';

import {buildArray, useNavigate} from 'shared/web';

import {TelephonyGroupCard} from './components/TelephonyGroupCard';
import {getLinesUsingGroup} from './utils/getLinesUsingGroup';

export function TelephonyGroups() {
  const navigate = useNavigate();

  const {
    data: groups,
    isLoading: areGroupsLoading,
    isError: areGroupsError,
  } = useGetTelephonyGroupsQuery();

  const {data: lines, isSuccess: areLinesLoaded} = useGetTelephonyLinesQuery();

  const [canWriteTelephonySettings] = usePermissions({
    permissionKeys: ['telephonySettingsWrite'],
  });

  const handleCreateGroup = () => navigate(settingsRoutes.telephonyGroupCreate);

  const headerActions = buildArray<Action>().when(canWriteTelephonySettings, {
    type: 'button',
    title: i18n.t('page.settings.telephony.newGroup'),
    onClick: handleCreateGroup,
    'data-testid': testIds.settings.telephonyGroups('newGroup'),
  });

  const emptyStateAction = canWriteTelephonySettings
    ? {
        title: i18n.t('page.settings.telephony.newGroup'),
        leftIcon: 'content/add_circle' as const,
        variant: 'link' as const,
        onClick: handleCreateGroup,
      }
    : undefined;

  // Usage stays unknown (null) until the lines load, so a group in use never reads as unused.
  const getUsedByLines = (group: TelephonyGroup) =>
    areLinesLoaded && isNotNil(lines) ? getLinesUsingGroup(lines.lines, group.id) : null;

  return (
    <SettingsTemplate
      data-testid={testIds.settings.telephonyGroups('settingsTemplate')}
      header={{
        title: i18n.t('page.settings.telephony.groupsTitle'),
        breadcrumbs: [{label: i18n.t('page.settings.telephony.title')}],
        actions: headerActions,
      }}
    >
      <SettingsSection>
        <VStack spacing={4}>
          <Text size="small" color="secondary">
            {i18n.t('page.settings.telephony.groupsDescription')}
          </Text>
          <DataStatus
            isLoading={areGroupsLoading}
            isError={areGroupsError}
            isEmpty={isEmpty(groups?.groups ?? [])}
            emptyMessage={i18n.t('page.settings.telephony.groupsEmptyHeadline')}
            emptySubheadline={i18n.t('page.settings.telephony.groupsEmptySubheadline')}
            action={emptyStateAction}
            minHeight={60}
            data-testid={testIds.settings.telephonyGroups('groups')}
          >
            <VStack spacing={4}>
              {groups?.groups.map((group, index) => (
                <TelephonyGroupCard
                  key={group.id}
                  group={group}
                  usedByLines={getUsedByLines(group)}
                  isReadOnly={!canWriteTelephonySettings}
                  data-testid={testIds.settings.telephonyGroups(`group-[${index}]`)}
                />
              ))}
            </VStack>
          </DataStatus>
        </VStack>
      </SettingsSection>
    </SettingsTemplate>
  );
}
